import { useEffect, useState } from "react";
import { Button, Input, Modal, DialogFooter, Loader } from "@/ui/atoms";
import { FormField } from "@/ui/molecules";

/** Mirrors MAX_SPLIT_CHILDREN_PER_CALL on the backend. */
export const MAX_SPLIT_COUNT = 50;
const MIN_SPLIT_COUNT = 1;
const DEFAULT_SPLIT_COUNT = 2;

export type TSplitCountModalProps = {
    open: boolean;
    /** Row number of the line being split, shown so the user can confirm the target. */
    rowNumber?: number;
    isSaving?: boolean;
    error?: string | null;
    onSubmit: (count: number) => void;
    onClose: () => void;
};

/**
 * Asks how many split rows to create for a line item.
 *
 * The rows are created at amount 0, so the line and its split rows are invalid
 * until the reviewer edits the amounts to sum to the original — that is the
 * expected next step, not an error in this modal.
 */
export function SplitCountModal({
    open,
    rowNumber,
    isSaving = false,
    error,
    onSubmit,
    onClose,
}: TSplitCountModalProps) {
    const [value, setValue] = useState<string>(String(DEFAULT_SPLIT_COUNT));

    // Re-seed whenever the modal (re)opens, so a previous entry never carries over.
    useEffect(() => {
        if (open) setValue(String(DEFAULT_SPLIT_COUNT));
    }, [open]);

    const parsed = Number(value);
    const isValid =
        value.trim() !== "" &&
        Number.isInteger(parsed) &&
        parsed >= MIN_SPLIT_COUNT &&
        parsed <= MAX_SPLIT_COUNT;

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!isValid) return;
        onSubmit(parsed);
    };

    return (
        <Modal
            open={open}
            onOpenChange={(next) => !next && onClose()}
            title="Split Line Item"
            description={
                rowNumber !== undefined
                    ? `Choose how many split rows to create for row ${rowNumber}.`
                    : "Choose how many split rows to create."
            }
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                    <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                        {error}
                    </div>
                )}
                <FormField label="Number of splits" htmlFor="split-count" required>
                    <Input
                        id="split-count"
                        data-testid="split-count-input"
                        aria-label="Number of splits"
                        type="number"
                        min={MIN_SPLIT_COUNT}
                        max={MAX_SPLIT_COUNT}
                        step={1}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                    />
                </FormField>
                <p className="text-xs text-muted-foreground">
                    Each split row starts with an amount of 0. The line stays invalid until the
                    split rows add up to the original amount.
                </p>
                <DialogFooter className="gap-2 pt-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={isSaving}
                        data-testid="split-count-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSaving || !isValid}
                        data-testid="split-count-save"
                    >
                        {isSaving ? (
                            <>
                                <Loader size="sm" className="mr-2" />
                                Saving...
                            </>
                        ) : (
                            "Save"
                        )}
                    </Button>
                </DialogFooter>
            </form>
        </Modal>
    );
}
